/**
 * Memorial workspace controls — tabbed private memorial configuration.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  ExternalLink, Image, Loader2, Save, Sparkles,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { getApiBase, getBackendBaseUrl } from '../../utils/apiBase';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import {
  MEMORIAL_PHOTO_LIMIT,
  MEMORIAL_SUPPORT_TYPES,
  mergePublicSections,
} from '../../utils/memorial';
import { MemorialKeepsakesPanel } from './MemorialKeepsakesPanel';
import { MemorialAppearancePanel } from './MemorialAppearancePanel';
import { MemorialTributesPanel } from './MemorialTributesPanel';
import { MemorialMusicLinksPanel } from './MemorialMusicLinksPanel';
import { MemorialVideoLinksPanel } from './MemorialVideoLinksPanel';
import {
  appearancePatchPayload,
  ATMOSPHERE_FIELD_LABELS,
  DEFAULT_APPEARANCE_RESET,
  getDefaultAppearanceForm,
} from '../../utils/memorialAppearance';
import {
  musicLinksPayload,
  normalizeMusicLinksForForm,
  normalizeVideoLinksForForm,
  videoLinksPayload,
} from '../../utils/musicLink';

const API = getApiBase();
const BACKEND = getBackendBaseUrl();

const WORKSPACE_TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'appearance', label: 'Appearance' },
  { id: 'content', label: 'Content' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'tributes', label: 'Tributes' },
  { id: 'media', label: 'Music & Video' },
  { id: 'qr', label: 'QR & Keepsakes' },
  { id: 'support', label: 'Support' },
  { id: 'privacy', label: 'Privacy' },
];

const SECTION_LABELS = {
  show_life_story: 'Life story',
  show_gallery: 'Gallery',
  show_tributes: 'Tributes',
  show_reactions: 'Quiet remembrance',
  show_voice: 'Voice memories',
  show_soundtrack: 'Soundtrack',
  show_videos: 'Videos',
  show_qr: 'QR code',
  show_keepsakes: 'Keepsakes',
  show_support: 'Support link',
  show_atmosphere: 'Atmosphere / senses',
};

function WorkspacePanel({ title, description, children, testId }) {
  return (
    <div className="rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[hsl(42_28%_99%)]/85 p-5" data-testid={testId}>
      <h3 className="font-serif text-base font-light text-[#1c1917]">{title}</h3>
      {description ? <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground/90">{description}</p> : null}
      <div className="mt-4 space-y-4">{children}</div>
    </div>
  );
}

export function MemorialWorkspaceControls({
  person,
  memorial,
  canEdit = false,
  publicUrl = null,
  onUpdated,
  onOpenVisibility,
  onPublishMemorial,
  publishing = false,
}) {
  const [tab, setTab] = useState('overview');
  const [form, setForm] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [saving, setSaving] = useState(false);
  const [appearanceSaveStatus, setAppearanceSaveStatus] = useState('idle');
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [draftingBio, setDraftingBio] = useState(false);
  const [biographyEdit, setBiographyEdit] = useState('');

  useEffect(() => {
    if (!memorial) {
      setForm(null);
      return;
    }
    const sections = mergePublicSections(memorial.public_sections);
    setForm({
      title: memorial.title || '',
      subtitle: memorial.subtitle || '',
      intro_message: memorial.intro_message || '',
      allow_tributes: memorial.allow_tributes !== false,
      moderate_tributes: memorial.moderate_tributes !== false,
      require_email_for_tributes: Boolean(memorial.require_email_for_tributes),
      allow_photos: Boolean(memorial.allow_photos),
      allow_voice: Boolean(memorial.allow_voice),
      allow_treasured_things: Boolean(memorial.allow_treasured_things),
      enable_reactions: memorial.enable_reactions !== false,
      show_reaction_counts: memorial.show_reaction_counts !== false,
      gallery_document_ids: memorial.gallery_document_ids || [],
      support_enabled: Boolean(memorial.support_enabled),
      support_label: memorial.support_label || 'Support the family',
      support_url: memorial.support_url || '',
      support_note: memorial.support_note || '',
      support_type: memorial.support_type || 'family',
      published_biography: memorial.published_biography || person?.biographical_notes || '',
      music_links: normalizeMusicLinksForForm(memorial),
      video_links: normalizeVideoLinksForForm(memorial.video_links),
      public_sections: sections,
      ...getDefaultAppearanceForm({ ...memorial, portrait_photo_url: memorial.portrait_photo_url || person?.profile_image_url || '' }),
    });
    setBiographyEdit(
      memorial.biography_draft?.text || memorial.published_biography || person?.biographical_notes || '',
    );
  }, [memorial, person?.profile_image_url, person?.biographical_notes]);

  const loadDocuments = useCallback(async () => {
    if (!person?.id) return;
    setLoadingDocs(true);
    try {
      const { data } = await axios.get(`${API}/documents/${person.id}`);
      setDocuments((data || []).filter((d) => !d.archived && d.thumbnail_url));
    } catch {
      setDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  }, [person?.id]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  if (!memorial || memorial.status === 'disabled' || !form) return null;
  if (!canEdit) return null;

  const patch = async (partial) => {
    if (!memorial?.id) return;
    setSaving(true);
    try {
      const payload = { ...partial };
      if (payload.cover_photo_url === '') payload.cover_photo_url = null;
      if (payload.portrait_photo_url === '') payload.portrait_photo_url = null;
      await axios.patch(`${API}/memorials-api/${memorial.id}`, payload);
      toast.success('Memorial settings saved.');
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save memorial settings.');
    } finally {
      setSaving(false);
    }
  };

  const saveAll = () => patch({
    ...form,
    published_biography: form.published_biography?.trim() || null,
    music_links: musicLinksPayload(form.music_links),
    video_links: videoLinksPayload(form.video_links),
  });

  const saveAppearance = async () => {
    if (!memorial?.id) return;
    setSaving(true);
    setAppearanceSaveStatus('saving');
    try {
      const payload = appearancePatchPayload(form);
      await axios.patch(`${API}/memorials-api/${memorial.id}`, payload);
      setAppearanceSaveStatus('saved');
      toast.success('Appearance saved.');
      onUpdated?.();
      setTimeout(() => setAppearanceSaveStatus('idle'), 2500);
    } catch (err) {
      setAppearanceSaveStatus('error');
      toast.error(err?.response?.data?.detail || 'Could not save appearance.');
    } finally {
      setSaving(false);
    }
  };

  const resetAppearance = async () => {
    if (!memorial?.id) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/memorials-api/${memorial.id}`, DEFAULT_APPEARANCE_RESET);
      setForm((prev) => ({
        ...prev,
        theme_preset: 'classic',
        theme_accent_colour: '',
        background_style: 'theme_default',
        wallpaper_url: '',
        wallpaper_preset: '',
        candle_style: 'classic',
        soundtrack_note: '',
        atmosphere_enabled: false,
        atmosphere: Object.fromEntries(
          Object.keys(ATMOSPHERE_FIELD_LABELS).map((k) => [k, '']),
        ),
      }));
      toast.success('Appearance reset to defaults.');
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not reset appearance.');
    } finally {
      setSaving(false);
    }
  };

  const toggleGalleryId = (docId) => {
    setForm((prev) => {
      const ids = prev.gallery_document_ids || [];
      const next = ids.includes(docId) ? ids.filter((x) => x !== docId) : [...ids, docId];
      if (next.length > MEMORIAL_PHOTO_LIMIT) {
        toast.message(`Gallery prepares for up to ${MEMORIAL_PHOTO_LIMIT} photos on memorial plans.`);
      }
      return { ...prev, gallery_document_ids: next.slice(0, MEMORIAL_PHOTO_LIMIT) };
    });
  };

  const toggleSection = (key, value) => {
    setForm((prev) => ({
      ...prev,
      public_sections: { ...prev.public_sections, [key]: value },
    }));
  };

  const draftBiography = async () => {
    setDraftingBio(true);
    try {
      const { data } = await axios.post(`${API}/memorials-api/${memorial.id}/draft-biography`);
      const text = data.draft?.text || '';
      setBiographyEdit(text);
      toast.success('Biography draft ready for your review.', {
        description: data.draft?.ai_generated ? 'AI polished from archive sources — please verify every detail.' : 'Assembled from archive sources.',
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not draft biography.');
    } finally {
      setDraftingBio(false);
    }
  };

  const applyBiography = async (saveToProfile = false) => {
    const text = biographyEdit.trim();
    if (!text) {
      toast.error('Add biography text before publishing.');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/memorials-api/${memorial.id}/apply-biography`, {
        text,
        save_to_profile: saveToProfile,
      });
      setForm((p) => ({ ...p, published_biography: text }));
      toast.success('Biography saved to the memorial page.');
      onUpdated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save biography.');
    } finally {
      setSaving(false);
    }
  };

  const photoOptions = [
    ...(person?.profile_image_url
      ? [{ value: person.profile_image_url, label: 'Profile photo' }]
      : []),
    ...documents.map((d) => ({
      value: d.thumbnail_url || d.file_url,
      label: d.title || 'Document photo',
      id: d.id,
    })),
  ];

  const personName = [person?.given_names, person?.surname].filter(Boolean).join(' ');

  return (
    <div className="space-y-5" data-testid="memorial-workspace-controls">
      <div className="overflow-x-auto border-b border-[hsl(38_14%_86%)]/70">
        <div className="flex min-w-max gap-1 pb-1">
          {WORKSPACE_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                'rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                tab === t.id
                  ? 'bg-[hsl(38_36%_42%)] text-white'
                  : 'text-muted-foreground hover:bg-muted/50',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {tab === 'overview' ? (
        <WorkspacePanel title="Page copy" description="How visitors first encounter their memorial." testId="memorial-controls-copy">
          <div className="space-y-2">
            <Label htmlFor="mem-title">Page title</Label>
            <Input id="mem-title" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mem-sub">Tribute line</Label>
            <Input id="mem-sub" value={form.subtitle} onChange={(e) => setForm((p) => ({ ...p, subtitle: e.target.value }))} placeholder="Remembered with love" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="mem-intro">Intro message</Label>
            <Textarea id="mem-intro" value={form.intro_message || ''} onChange={(e) => setForm((p) => ({ ...p, intro_message: e.target.value }))} rows={3} />
          </div>
        </WorkspacePanel>
      ) : null}

      {tab === 'appearance' ? (
        <MemorialAppearancePanel
          form={form}
          setForm={setForm}
          photoOptions={photoOptions}
          publicUrl={publicUrl}
          backendBase={BACKEND}
          saving={saving}
          saveStatus={appearanceSaveStatus}
          onSaveAppearance={saveAppearance}
          onResetAppearance={resetAppearance}
        />
      ) : null}

      {tab === 'content' ? (
        <WorkspacePanel title="Life story" description="Approved biography appears on the public memorial when Life story is enabled." testId="memorial-controls-bio">
          <p className="text-xs text-muted-foreground">
            Draft with AI from life summary, stories, and voice transcripts — then review every detail before publishing.
          </p>
          <Button type="button" variant="outline" size="sm" className="rounded-full" disabled={draftingBio} onClick={draftBiography} data-testid="memorial-draft-bio-btn">
            {draftingBio ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-2 h-3.5 w-3.5" />}
            Draft biography with AI
          </Button>
          <Textarea
            value={biographyEdit}
            onChange={(e) => setBiographyEdit(e.target.value)}
            rows={10}
            className="font-serif text-sm leading-relaxed"
            placeholder="Write or paste an approved biography…"
          />
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" className="rounded-full" disabled={saving} onClick={() => applyBiography(false)}>
              Save to memorial page
            </Button>
            <Button type="button" size="sm" variant="outline" className="rounded-full" disabled={saving} onClick={() => applyBiography(true)}>
              Save to memorial & profile
            </Button>
          </div>
          {onOpenVisibility ? (
            <Button type="button" variant="ghost" size="sm" className="rounded-full" onClick={onOpenVisibility}>
              Configure archive visibility (stories, timeline)
            </Button>
          ) : null}
        </WorkspacePanel>
      ) : null}

      {tab === 'gallery' ? (
        <WorkspacePanel title="Photo gallery" description={`Choose public photos — prepared for up to ${MEMORIAL_PHOTO_LIMIT} on memorial plans.`} testId="memorial-controls-gallery">
          <label className="flex items-center justify-between gap-3">
            <span className="text-sm">Enable gallery</span>
            <Switch checked={form.allow_photos} onCheckedChange={(v) => setForm((p) => ({ ...p, allow_photos: v }))} />
          </label>
          <label className="flex items-center justify-between gap-3">
            <span className="text-sm">Treasured keepsakes</span>
            <Switch checked={form.allow_treasured_things} onCheckedChange={(v) => setForm((p) => ({ ...p, allow_treasured_things: v }))} />
          </label>
          {form.allow_photos ? (
            <div className="space-y-2 border-t border-border/30 pt-3">
              <Label>Gallery photos {loadingDocs ? '(loading…)' : ''}</Label>
              <p className="text-[11px] text-muted-foreground">Select photos for the public gallery. Unchecked = all eligible photos.</p>
              {documents.length === 0 && !loadingDocs ? (
                <p className="text-xs text-muted-foreground">Add photos on the Documents tab first.</p>
              ) : (
                <ul className="max-h-52 space-y-1 overflow-y-auto">
                  {documents.map((d) => (
                    <li key={d.id}>
                      <label className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-muted/40">
                        <input type="checkbox" checked={(form.gallery_document_ids || []).includes(d.id)} onChange={() => toggleGalleryId(d.id)} />
                        {d.thumbnail_url ? (
                          <img src={resolveAssetUrl(BACKEND, d.thumbnail_url)} alt="" className="h-8 w-8 rounded object-cover" />
                        ) : (
                          <Image className="h-4 w-4 text-muted-foreground" />
                        )}
                        <span className="truncate">{d.title || 'Photo'}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </WorkspacePanel>
      ) : null}

      {tab === 'tributes' ? (
        <MemorialTributesPanel
          memorialId={memorial.id}
          form={form}
          setForm={setForm}
          onUpdated={onUpdated}
        />
      ) : null}

      {tab === 'media' ? (
        <div className="space-y-5">
          <MemorialMusicLinksPanel form={form} setForm={setForm} />
          <MemorialVideoLinksPanel form={form} setForm={setForm} />
        </div>
      ) : null}

      {tab === 'qr' ? (
        <MemorialKeepsakesPanel
          memorial={memorial}
          publicUrl={publicUrl}
          personName={personName}
          person={person}
          onPublishMemorial={canEdit ? onPublishMemorial : undefined}
          publishing={publishing}
        />
      ) : null}

      {tab === 'support' ? (
        <WorkspacePanel title="External support link" description="MyLegacyBridge does not collect donations directly yet." testId="memorial-controls-support">
          <label className="flex items-center justify-between gap-3">
            <span className="text-sm">Show support link</span>
            <Switch checked={form.support_enabled} onCheckedChange={(v) => setForm((p) => ({ ...p, support_enabled: v }))} />
          </label>
          {form.support_enabled ? (
            <>
              <div className="space-y-2">
                <Label>Link type</Label>
                <Select value={form.support_type} onValueChange={(v) => setForm((p) => ({ ...p, support_type: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(MEMORIAL_SUPPORT_TYPES).map(([k, label]) => (
                      <SelectItem key={k} value={k}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="support-label">Button label</Label>
                <Input id="support-label" value={form.support_label} onChange={(e) => setForm((p) => ({ ...p, support_label: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="support-url">External URL</Label>
                <Input id="support-url" type="url" value={form.support_url} onChange={(e) => setForm((p) => ({ ...p, support_url: e.target.value }))} placeholder="https://…" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="support-note">Short note</Label>
                <Textarea id="support-note" value={form.support_note || ''} onChange={(e) => setForm((p) => ({ ...p, support_note: e.target.value }))} rows={2} />
              </div>
              <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                <ExternalLink className="mt-0.5 h-3 w-3 shrink-0" />
                This link opens an external support page chosen by the family.
              </p>
            </>
          ) : null}
        </WorkspacePanel>
      ) : null}

      {tab === 'privacy' ? (
        <WorkspacePanel title="Public section visibility" description="Nothing appears until you publish — and only sections you enable." testId="memorial-controls-privacy">
          {Object.entries(SECTION_LABELS).map(([key, label]) => (
            <label key={key} className="flex items-center justify-between gap-3">
              <span className="text-sm">{label}</span>
              <Switch
                checked={Boolean(form.public_sections?.[key])}
                onCheckedChange={(v) => toggleSection(key, v)}
              />
            </label>
          ))}
          {onOpenVisibility ? (
            <Button type="button" variant="outline" size="sm" className="mt-2 rounded-full" onClick={onOpenVisibility}>
              Archive content visibility (stories, biography source)
            </Button>
          ) : null}
        </WorkspacePanel>
      ) : null}

      {tab !== 'qr' && tab !== 'appearance' ? (
        <Button type="button" className="rounded-full" disabled={saving} onClick={saveAll} data-testid="memorial-controls-save">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Save memorial settings
        </Button>
      ) : null}
    </div>
  );
}

export default MemorialWorkspaceControls;
